import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getUserPurchases } from 'actions/profile';
import PurchaseDetailModal from '../PurchaseDetailModal';
import styles from '../ProfileStyles.module.scss';

const PurchaseHistory = () => {
    const dispatch = useDispatch();
    const { purchases, purchasesLoading } = useSelector(state => state.profile);
    const [selectedPurchase, setSelectedPurchase] = useState(null);

    useEffect(() => {
        dispatch(getUserPurchases());
    }, [dispatch]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return `£${amount.toFixed(2)}`;
    };

    const getItemsSummary = (items) => {
        if (!items || items.length === 0) return 'No items';
        if (items.length === 1) return items[0].name;
        return `${items[0].name} + ${items.length - 1} more`;
    };

    if (purchasesLoading) {
        return (
            <div>
                <h2 className={styles.sectionTitle}>Purchase History</h2>
                <div className={styles.loading}>
                    <div className={styles.loadingSpinner}></div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h2 className={styles.sectionTitle}>Purchase History</h2>
            <p className={styles.sectionDescription}>
                A history of all your purchases. Click any purchase to view details and activation codes.
            </p>

            {purchases.length === 0 ? (
                <div className={styles.card}>
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>💳</div>
                        <h3 className={styles.emptyTitle}>No Purchases Yet</h3>
                        <p className={styles.emptyText}>
                            You haven't made any purchases yet.
                            Visit our store to browse available games.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Desktop table view */}
                    <div className={`${styles.card} ${styles.desktopOnly}`}>
                        <table className={styles.vouchersTable}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Items</th>
                                    <th>Total</th>
                                    <th>Discount</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {purchases.map((purchase, index) => (
                                    <tr
                                        key={index}
                                        className={styles.purchaseRow}
                                        onClick={() => setSelectedPurchase(purchase)}
                                    >
                                        <td>{formatDate(purchase.date)}</td>
                                        <td>{getItemsSummary(purchase.items)}</td>
                                        <td className={styles.purchaseTotal}>
                                            {formatCurrency(purchase.total)}
                                        </td>
                                        <td>
                                            {purchase.discountCode ? (
                                                <span className={styles.discountBadge}>
                                                    {purchase.discountCode}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td>
                                            <span className={styles.viewButton}>View</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile card view */}
                    <div className={styles.voucherCards}>
                        {purchases.map((purchase, index) => (
                            <div
                                key={index}
                                className={`${styles.card} ${styles.purchaseCard}`}
                                onClick={() => setSelectedPurchase(purchase)}
                            >
                                <div className={styles.voucherCardHeader}>
                                    <span className={styles.infoValue}>
                                        {formatDate(purchase.date)}
                                    </span>
                                    {purchase.discountCode && (
                                        <span className={styles.discountBadge}>
                                            {purchase.discountCode}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Items</span>
                                    <span className={styles.infoValue}>
                                        {getItemsSummary(purchase.items)}
                                    </span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Total</span>
                                    <span className={`${styles.infoValue} ${styles.purchaseTotal}`}>
                                        {formatCurrency(purchase.total)}
                                    </span>
                                </div>
                                <div className={styles.viewButtonWrapper}>
                                    <span className={styles.viewButton}>View Details</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {selectedPurchase && (
                <PurchaseDetailModal
                    purchase={selectedPurchase}
                    onClose={() => setSelectedPurchase(null)}
                    onRedeemed={() => {
                        // Refresh purchases to update redemption status
                        dispatch(getUserPurchases());
                    }}
                />
            )}
        </div>
    );
};

export default PurchaseHistory;
